import { BranchKeystore } from './branch';
import { LocationAttributes } from './location';

export interface Platform {
  name: string;
  branchAliasId: number;
  branchAliasName: string;
}

export class LocationAlias {
  id: number;
  name: string;
  platform: string;
  active: boolean;
  scrape: boolean;
  branchAliasId: number;

  constructor(id: number, branchAliasId: number, name: string, platform: string, active: boolean, scrape: boolean) {
    this.id = id;
    this.branchAliasId = branchAliasId;
    this.name = name;
    this.platform = platform;
    this.active = active;
    this.scrape = scrape;
  }
}

export interface KioskTransaction {
  branch_alias_name: string;
  adm_branch_alias_uuid: string; //this is NOT the branch alias id
  location_name: string;
  adm_location_uuid: string; //this is NOT the location alias id
  device: string;
  trans_id: string;
  trans_date: Date;
  date_created: Date;
  product_name: string;
  scan_code?: string | null;
  user_key?: string | null;
  sap_type: string;
  category_1: string;
  category_2?: string | null;
  category_3?: string | null;
  price: number; // all numbers from here are decimals
  tax_1: number;
  tax_2: number;
  tax_3: number;
  tax_4: number;
  tax_system: string;
  deposit: number;
  discount: number;
  cost: number;
  loyalty: number;
  type: 'sold' | 'cancellation';
}

export interface Kiosk {
  active: number;
  branch_alias_id: number;
  branch_alias_name: string;
  data_source: string;
  id: number;
  location_id: number;
  name: string;
  scrape: number;
  scrape_from: string;
  external_id?: string;
  external_branch_id?: string;
  unit_type?: string;
}

export type LocationProperty = 'lp_queue';
export type LocationProperties = Partial<Record<LocationProperty, string | null>>;

export interface LocationInformation {
  id: number;
  branchId: number;
  name: string;
  trialDuration: number;
  status: string;
  subscriptionStatusId: string;
  nextProductId: string;
  trialStartDate: string;
  external_id: string;
  location_group_id: string;
  locationAliasesMap: Map<string, LocationAlias>;
  locationAliasesMemo: Array<LocationAlias>;
  stripePriceId?: string;
  trialEnd?: Date;
  flagged?: boolean;
  flaggedReasons?: string[];
  flaggedAt?: Date;
  assignedCameras?: string[];
  timezone: string;
  attributes: LocationAttributes;
  properties: LocationProperties;
}

export class LocationImpl implements LocationInformation {
  id: number;
  branchId: number;
  name: string;
  trialDuration: number;
  external_id: string;
  location_group_id: string;
  status: string;
  subscriptionStatusId: string;
  nextProductId: string;
  trialStartDate: string;
  locationAliasesMap: Map<string, LocationAlias>;
  locationAliasesMemo: Array<LocationAlias>;
  stripePriceId?: string;
  trialEnd?: Date;
  flagged?: boolean;
  flaggedReasons?: string[];
  flaggedAt?: Date;
  assignedCameras?: string[];
  timezone: string;
  attributes: LocationAttributes;
  properties: LocationProperties;

  constructor(input: LocationOptions) {
    this.id = input.id;
    this.branchId = input.branchId;
    this.name = input.name;
    this.locationAliasesMap = new Map<string, LocationAlias>();
    this.locationAliasesMemo = new Array<LocationAlias>();
    this.stripePriceId = input.stripePriceId;
    this.trialEnd = input.trialEnd;
    this.flagged = input.flagged;
    this.flaggedReasons = input.flaggedReasons;
    this.flaggedAt = input.flaggedAt;
    this.status = input.status;
    this.assignedCameras = input.assigned_cameras;
    this.attributes = input.attributes ?? {};
    this.properties = input.properties ?? {};
  }

  addLocationAlias(id: number, branchAliasId: number, name: string, platform: string, active: boolean, scrape: boolean) {
    this.locationAliasesMap.set(this.getNameKey(name, platform), new LocationAlias(id, branchAliasId, name, platform, active, scrape));
    this.locationAliasesMemo = [...this.locationAliasesMap.values()];
    return this.locationAliasesMap.get(this.getNameKey(name, platform));
  }

  getNameKey(name: string, platform: string) {
    return `${name}::${platform}`;
  }

  getLocationAliases() {
    return this.locationAliasesMemo;
  }

  deleteLocationAlias(name: string, platform: string) {
    this.locationAliasesMap.delete(this.getNameKey(name, platform));
    this.locationAliasesMemo = [...this.locationAliasesMap.values()];
  }

  deleteLocationAliasByBranchAliasId(id) {
    const locationAliasesNames = [...this.locationAliasesMap.entries()].filter(([, value]) => value.branchAliasId === id).map(([k]) => k);
    for (const locationAliasName of locationAliasesNames) {
      this.locationAliasesMap.delete(locationAliasName);
    }
    this.locationAliasesMemo = [...this.locationAliasesMap.values()];
  }
}

export class BranchAlias {
  id: number;
  name: string;
  platform: string;

  constructor(id: number, name: string, platform: string) {
    this.id = id;
    this.name = name;
    this.platform = platform;
  }
}

export class Users {
  email: string;
  branch_id: number[] = [];
}

interface BranchOptions {
  id: number;
  name: string;
  faceRecognitionEnabled: boolean;
  keystore: string;
  stripeCustomerId?: string;
  customerType?: string;
  customerId?: string;
  lp_queue?: string;
}

interface LocationOptions {
  id: number;
  branchId?: number;
  name: string;
  stripePriceId?: string;
  trialEnd?: Date;
  flagged?: boolean;
  flaggedReasons?: string[];
  flaggedAt?: Date;
  status?: string;
  assigned_cameras?: string[];
  attributes?: LocationAttributes;
  properties?: LocationProperties;
}
export class BranchImpl {
  id: number;
  name: string;
  faceRecognitionEnabled: boolean;
  hideCurrency: boolean;
  requireQa: boolean;
  vip: boolean;
  platforms: string[] = [];
  keystore: BranchKeystore;
  lp_group_name?: string;
  lp_queue?: string;
  private locations: Map<string, LocationImpl>;
  private branchAliases: Map<string, BranchAlias>;
  public stripeCustomerId?: string;
  public customerType?: string;
  public customerId?: string;

  constructor(options: BranchOptions) {
    this.id = options.id;
    this.name = options.name;
    this.faceRecognitionEnabled = options.faceRecognitionEnabled;
    this.branchAliases = new Map<string, BranchAlias>();
    this.locations = new Map<string, LocationImpl>();
    this.stripeCustomerId = options.stripeCustomerId;
    this.customerType = options.customerType;
    this.customerId = options.customerId;
    this.lp_queue = options.lp_queue;
    try {
      this.keystore = JSON.parse(options.keystore) as BranchKeystore;
      this.hideCurrency = this.keystore.hide_currency === true;
      this.requireQa = this.keystore.incident_submission_requires_qa === true;
      this.vip = this.keystore.vip === true;
      this.lp_group_name = this.keystore.lp_group_name;
    } catch (err) {
      console.warn(err);
    }
  }

  addBranchAlias(id: number, name: string, platform: string): BranchAlias {
    const ba = new BranchAlias(id, name, platform);
    this.branchAliases.set(`${name}+${platform}`, ba);
    if (!this.platforms.includes(platform)) {
      this.platforms.push(platform);
    }
    return ba;
  }

  getBranchAliases(): BranchAlias[] {
    return [...this.branchAliases.values()];
  }

  addLocation(options: LocationOptions) {
    const l = this.locations.get(options.name);
    if (l) {
      return l;
    } else {
      this.locations.set(options.name, new LocationImpl(options));
      return this.locations.get(options.name);
    }
  }

  getLocations(): LocationImpl[] {
    return [...this.locations.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  getLocationAliases() {
    const locations = this.getLocations();
    const locationAliases = [];
    for (const l of locations) {
      locationAliases.push(...l.getLocationAliases());
    }
    return locationAliases;
  }

  deleteLocationByName(name: string) {
    this.locations.delete(name);
  }
}
